import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { useEnqueueMaster, useMasters } from '../../lib/data/queries.js';
import { getSyncEngine } from '../../lib/sync/engine.js';
import { getSupabase } from '../../lib/supabase.js';
import { readSalesFile, type SalesSheet } from '../../lib/salesFile.js';
import {
  normalizeSales,
  salesImportConflict,
  salesNumber,
  salesDate,
  SalesMappingSchema,
  suggestSalesMapping,
  summarizeSales,
  type SalesImport,
  type SalesMapping,
} from '../../lib/salesImport.js';
import './SalesImportPanel.css';

const euro = (value: number | null) =>
  value === null
    ? 'Non disponibile'
    : new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);

export function SalesImportPanel() {
  const { profile } = useAuth();
  if (
    !profile?.active_tenant_id ||
    !(profile.is_platform_owner || ['consulente', 'ristoratore'].includes(profile.role ?? ''))
  )
    return <p>Importazione vendite riservata al ristoratore e al consulente.</p>;
  return (
    <SalesImportWorkspace
      key={profile.active_tenant_id}
      tenantId={profile.active_tenant_id}
      initialBusiness={profile.active_business_id ?? ''}
    />
  );
}

function SalesImportWorkspace({
  tenantId,
  initialBusiness,
}: {
  tenantId: string;
  initialBusiness: string;
}) {
  const batchesQuery = useMasters<SalesImport>('sales_imports', { includeArchived: true });
  const batches = batchesQuery.data ?? [];
  const { data: businesses = [] } = useMasters<{ id: string; name: string }>('businesses');
  const { data: recipes = [] } = useMasters<{ id: string; name: string }>('recipes');
  const enqueue = useEnqueueMaster();
  const qc = useQueryClient();
  const [business, setBusiness] = useState(initialBusiness);
  const [source, setSource] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [loaded, setLoaded] = useState<{
    sheets: SalesSheet[];
    base64: string;
    sha: string;
  } | null>(null);
  const [delimiter, setDelimiter] = useState(';');
  const [encoding, setEncoding] = useState('utf-8');
  const [sheet, setSheet] = useState(0);
  const [header, setHeader] = useState(0);
  const [mapping, setMapping] = useState<SalesMapping | null>(null);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [excluded, setExcluded] = useState<number[]>([]);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [control, setControl] = useState('');
  const [detail, setDetail] = useState<string | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [rowPage, setRowPage] = useState(0);
  const grid = useMemo(() => loaded?.sheets[sheet]?.data ?? [], [loaded, sheet]);
  const preview = useMemo(
    () =>
      mapping
        ? normalizeSales(grid, header, mapping, start, end, excluded)
        : { lines: [], errors: [] },
    [grid, header, mapping, start, end, excluded],
  );
  const totals = summarizeSales(preview.lines);
  const conflict = loaded
    ? salesImportConflict(batches, business, source, loaded.sha, start, end)
    : null;
  const expected = mapping ? salesNumber(control, mapping.decimal) : null;
  const matchingTotal = expected !== null && Math.abs(expected - totals.amount) < 0.005;
  const shown = batches
    .filter((batch) => batch.business_id === business)
    .sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''));
  const sources = [...new Set(shown.map((batch) => batch.source))];
  const valid = shown.filter((batch) => batch.confirmed_at && !batch.archived_at);
  const update = (patch: Partial<SalesMapping>) => {
    if (mapping) setMapping({ ...mapping, ...patch });
    setConfirmed(false);
  };

  async function load() {
    if (!file) return;
    setBusy(true);
    setMessage('');
    setLoaded(null);
    setMapping(null);
    setConfirmed(false);
    try {
      const parsed = await readSalesFile(file, delimiter, encoding);
      setLoaded(parsed);
      setSheet(0);
      setHeader(0);
      setExcluded([]);
      setRowPage(0);
      const config = suggestSalesMapping(parsed.sheets[0]?.data[0] ?? []);
      if (/\.xlsx$/i.test(file.name)) config.decimal = 'point';
      const previous = shown.find(
        (batch) =>
          batch.confirmed_at &&
          !batch.archived_at &&
          SalesMappingSchema.safeParse(batch.mapping).success &&
          batch.source === source.trim().toLowerCase() &&
          JSON.stringify(batch.mapping.headers) === JSON.stringify(config.headers),
      );
      setMapping(previous?.mapping ?? config);
      if (previous)
        setMessage(
          'Formato riconosciuto: configurazione della fonte ripresa. Verifica comunque il nuovo periodo e il totale.',
        );
      setStep(2);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Lettura non riuscita.');
    } finally {
      setBusy(false);
    }
  }
  function reuse() {
    const previous = shown.find(
      (batch) =>
        batch.source === source.trim().toLowerCase() && batch.confirmed_at && !batch.archived_at,
    );
    if (!previous) {
      setMessage('Nessuna configurazione confermata per questa fonte.');
      return;
    }
    if (
      !SalesMappingSchema.safeParse(previous.mapping).success ||
      JSON.stringify(previous.mapping.headers) !== JSON.stringify(grid[header])
    ) {
      setMessage('Le intestazioni sono diverse: verifica una nuova configurazione.');
      return;
    }
    setMapping(previous.mapping);
    setConfirmed(false);
    setMessage('Configurazione ripresa. Controlla periodo, righe e totale prima di confermare.');
  }
  async function save() {
    if (
      !mapping ||
      !loaded ||
      !file ||
      !confirmed ||
      !matchingTotal ||
      preview.errors.length ||
      conflict ||
      !source.trim() ||
      !business
    )
      return;
    setBusy(true);
    setMessage('');
    const id = crypto.randomUUID();
    const patch = {
      tenant_id: tenantId,
      business_id: business,
      source: source.trim().toLowerCase(),
      filename: file.name,
      file_sha256: loaded.sha,
      period_start: start,
      period_end: end,
      mapping,
      lines: preview.lines,
      audit: {
        sheet: loaded.sheets[sheet].sheet,
        header_row: header + 1,
        excluded_rows: excluded.map((i) => i + 1),
        delimiter,
        encoding,
        source_total: expected,
        confirmed_semantics: true,
        currency: 'EUR',
      },
      original_base64: loaded.base64,
      amount_total: totals.amount,
      net_total: totals.net,
      missing_net: totals.missing,
    };
    try {
      if (new TextEncoder().encode(JSON.stringify(patch)).length > 2 * 1024 * 1024)
        throw new Error(
          'Estratto troppo grande dopo la lettura: suddividilo in periodi più brevi.',
        );
      await enqueue.mutateAsync({
        table: 'sales_imports',
        id,
        op: 'insert',
        base_version: 0,
        patch,
      });
      setLoaded(null);
      setMapping(null);
      setFile(null);
      setStep(1);
      setControl('');
      setConfirmed(false);
      setMessage(
        'Importazione salvata sul dispositivo. I ricavi entrano nell’analisi solo dopo la conferma del server.',
      );
      if (navigator.onLine) {
        const result = await getSyncEngine().confirmMasterPushed('sales_imports', id, {
          acceptConflict: false,
          expectedPatch: patch,
        });
        const receipt = await getSupabase()
          .from('sales_imports')
          .select('id,confirmed_at')
          .eq('id', id)
          .maybeSingle();
        if (receipt.data?.confirmed_at)
          setMessage('Importazione confermata dal server. Il totale è disponibile nello storico.');
        else
          setMessage(
            `Importazione in attesa di verifica: ${result.reason ?? 'sincronizzazione non completata'}. Non conteggiata nei ricavi.`,
          );
        await getSyncEngine().tick();
        await qc.invalidateQueries({ queryKey: ['masters', 'sales_imports'] });
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Salvataggio non riuscito.');
    } finally {
      setBusy(false);
    }
  }
  async function download(batch: SalesImport) {
    setBusy(true);
    try {
      const { data, error } = await getSupabase()
        .from('sales_import_files')
        .select('original_base64')
        .eq('id', batch.id)
        .single();
      if (error) throw error;
      const raw = atob(data.original_base64);
      const blob = new Blob([Uint8Array.from(raw, (c) => c.charCodeAt(0))], {
        type: /\.xlsx$/i.test(batch.filename)
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'text/csv',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = batch.filename;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      setMessage('File originale non disponibile: verifica connessione e accesso al locale.');
    } finally {
      setBusy(false);
    }
  }
  async function voidBatch(batch: SalesImport) {
    if (voidReason.trim().length < 5) return;
    setBusy(true);
    try {
      await enqueue.mutateAsync({
        table: 'sales_imports',
        id: batch.id,
        op: 'archive',
        base_version: batch.version,
        patch: {
          tenant_id: tenantId,
          archived_at: new Date().toISOString(),
          void_reason: voidReason,
        },
      });
      if (navigator.onLine) await getSyncEngine().tick();
      await qc.invalidateQueries({ queryKey: ['masters', 'sales_imports'] });
      setVoidReason('');
      setDetail(null);
      setMessage(
        'Richiesta di annullamento salvata e in sincronizzazione; originale e storico conservati. Attendi la conferma del server prima di caricare il sostitutivo.',
      );
    } catch {
      setMessage('Annullamento non confermato. Verifica la connessione e riprova.');
    } finally {
      setBusy(false);
    }
  }
  const column = (key: 'date' | 'item' | 'quantity' | 'amount' | 'vat', label: string) => (
    <label key={key}>
      {label}
      <select
        value={mapping?.[key] ?? -1}
        onChange={(e) => update({ [key]: Number(e.target.value) })}
      >
        <option value={-1}>Non presente</option>
        {mapping?.headers.map((name, i) => (
          <option key={i} value={i}>
            {i + 1}. {name || 'Senza titolo'}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <section className="sales-import">
      <div className="fc-section-heading">
        <h1>Vendite e conto economico</h1>
        <p>
          Carica gli estratti della cassa e verifica come interpretarli. Una configurazione per ogni
          fonte del cliente.
        </p>
      </div>
      <label>
        Locale
        <select
          value={business}
          disabled={busy || step !== 1}
          onChange={(e) => {
            setBusiness(e.target.value);
            setSource('');
            setDetail(null);
          }}
        >
          <option value="">Seleziona il locale</option>
          {businesses.map((b) => (
            <option value={b.id} key={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </label>
      {step === 1 && (
        <section className="fc-card">
          <h2>Dati disponibili</h2>
          <p>
            {valid.length}{' '}
            {valid.length === 1 ? 'importazione confermata' : 'importazioni confermate'} per questo
            locale. I periodi possono essere diversi: consulta i totali di ciascun estratto sotto.
          </p>
          <p>
            Il risultato economico non è ancora disponibile: servono inventari e costi di gestione.
            Le vendite non scaricano automaticamente il magazzino.
          </p>
        </section>
      )}
      <fieldset className="fc-card sales-wizard" disabled={busy}>
        <h2>Importa vendite</h2>
        <p className="sales-steps">
          {step === 1
            ? '1. Carica il file'
            : step === 2
              ? '2. Verifica il formato'
              : '3. Conferma i dati'}
        </p>
        {step === 1 && (
          <>
            <label>
              Fonte delle vendite
              <input
                list="sales-sources"
                maxLength={100}
                value={source}
                onChange={(e) => setSource(e.target.value)}
                placeholder="Esempio: Cassa principale"
              />
            </label>
            <datalist id="sales-sources">
              {sources.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
            <p>
              Usa la stessa fonte per estratti giornalieri e mensili della stessa cassa. Non
              importare come fonti separate riepiloghi che contengono le stesse vendite.
            </p>
            <label>
              File vendite
              <input
                type="file"
                accept=".csv,.tsv,.xlsx"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </label>
            <p>
              CSV, TSV o Excel XLSX, massimo 1 MB e 5.000 righe. PDF, foto e vecchi XLS non sono
              ancora importabili. Non includere nomi dei clienti o dati di pagamento non necessari.
            </p>
            <div className="sales-fields">
              <label>
                Separatore CSV
                <select value={delimiter} onChange={(e) => setDelimiter(e.target.value)}>
                  <option value=";">Punto e virgola</option>
                  <option value=",">Virgola</option>
                  <option value={'\t'}>Tabulazione</option>
                </select>
              </label>
              <label>
                Codifica CSV
                <select value={encoding} onChange={(e) => setEncoding(e.target.value)}>
                  <option value="utf-8">UTF-8</option>
                  <option value="windows-1252">Windows / Excel italiano</option>
                </select>
              </label>
            </div>
            <button
              className="sales-primary"
              disabled={busy || !file || !business || !source.trim()}
              onClick={() => void load()}
            >
              {busy ? 'Lettura…' : 'Leggi e prepara anteprima'}
            </button>
          </>
        )}
        {step === 2 && mapping && (
          <>
            <p>
              <strong>{file?.name}</strong> · {source}
            </p>
            <div className="sales-fields">
              <label>
                Foglio
                <select
                  value={sheet}
                  onChange={(e) => {
                    const index = Number(e.target.value);
                    setSheet(index);
                    setRowPage(0);
                    setHeader(0);
                    setExcluded([]);
                    setMapping({
                      ...suggestSalesMapping(loaded!.sheets[index].data[0] ?? []),
                      decimal: /\.xlsx$/i.test(file?.name ?? '') ? 'point' : 'comma',
                    });
                  }}
                >
                  {loaded?.sheets.map((s, i) => (
                    <option value={i} key={i}>
                      {s.sheet}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Riga delle intestazioni
                <select
                  value={header}
                  onChange={(e) => {
                    const index = Number(e.target.value);
                    setHeader(index);
                    setRowPage(0);
                    setExcluded([]);
                    setMapping({ ...suggestSalesMapping(grid[index]), decimal: mapping.decimal });
                  }}
                >
                  {grid.slice(0, 30).map((row, i) => (
                    <option value={i} key={i}>
                      {i + 1}. {row.slice(0, 3).join(' / ').slice(0, 90)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <button onClick={reuse}>Riprendi configurazione della fonte</button>
            {mapping.date >= 0 && (
              <button
                onClick={() => {
                  const dates = grid
                    .slice(header + 1)
                    .map((row) => salesDate(row[mapping.date] ?? '', mapping.dateFormat))
                    .filter((date): date is string => date !== null)
                    .sort();
                  if (dates.length) {
                    setStart(dates[0]);
                    setEnd(dates[dates.length - 1]);
                    setConfirmed(false);
                  }
                }}
              >
                Usa le date presenti nel file
              </button>
            )}
            <div className="sales-fields">
              <label>
                Dal
                <input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
              </label>
              <label>
                Al
                <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
              </label>
              <label>
                Contenuto dell’estratto
                <select
                  value={mapping.grain}
                  onChange={(e) => update({ grain: e.target.value as SalesMapping['grain'] })}
                >
                  <option value="items">Vendite per articolo</option>
                  <option value="totals">Solo totali / reparti</option>
                </select>
              </label>
              <label>
                Importi della colonna totale
                <select
                  value={mapping.basis}
                  onChange={(e) => update({ basis: e.target.value as SalesMapping['basis'] })}
                >
                  <option value="gross">IVA inclusa</option>
                  <option value="net">IVA esclusa</option>
                </select>
              </label>
              <label>
                Formato numeri
                <select
                  value={mapping.decimal}
                  onChange={(e) => update({ decimal: e.target.value as SalesMapping['decimal'] })}
                >
                  <option value="comma">1.234,56</option>
                  <option value="point">1,234.56 (numeri Excel)</option>
                </select>
              </label>
              <label>
                Formato date
                <select
                  value={mapping.dateFormat}
                  onChange={(e) =>
                    update({ dateFormat: e.target.value as SalesMapping['dateFormat'] })
                  }
                >
                  <option value="dmy">Giorno / mese / anno</option>
                  <option value="mdy">Mese / giorno / anno</option>
                  <option value="iso">Anno-mese-giorno</option>
                </select>
              </label>
              {column('amount', 'Totale di riga (non prezzo unitario)')}
              {column('date', 'Data vendita')}
              {mapping.grain === 'items' && column('item', 'Descrizione articolo')}
              {mapping.grain === 'items' && column('quantity', 'Quantità venduta')}
              {column('vat', 'Aliquota IVA (%)')}
              {mapping.vat < 0 && (
                <label>
                  IVA unica, solo se nota (%)
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step="0.01"
                    value={mapping.vatDefault ?? ''}
                    onChange={(e) =>
                      update({ vatDefault: e.target.value === '' ? null : Number(e.target.value) })
                    }
                  />
                </label>
              )}
            </div>
            <p>
              Gli importi devono essere già al netto di sconti e resi. I resi devono avere segno
              negativo. Se manca la data di riga, vale solo il periodo dichiarato; senza IVA nota il
              ricavo netto resta non disponibile.
            </p>
            <details>
              <summary>Righe originali ed esclusioni ({grid.length - header - 1})</summary>
              <p>
                Escludi esplicitamente totali, subtotali e righe di servizio. Le esclusioni vengono
                conservate nello storico.
              </p>
              <label>
                Pagina righe
                <select value={rowPage} onChange={(e) => setRowPage(Number(e.target.value))}>
                  {Array.from({ length: Math.ceil((grid.length - header - 1) / 50) }, (_, i) => (
                    <option key={i} value={i}>
                      {i * 50 + 1}–{Math.min((i + 1) * 50, grid.length - header - 1)}
                    </option>
                  ))}
                </select>
              </label>
              <div className="sales-raw">
                {grid
                  .slice(header + 1 + rowPage * 50, header + 1 + (rowPage + 1) * 50)
                  .map((row, j) => {
                    const i = header + 1 + rowPage * 50 + j;
                    return (
                      <label key={i}>
                        <input
                          type="checkbox"
                          checked={excluded.includes(i)}
                          onChange={(e) =>
                            setExcluded(
                              e.target.checked ? [...excluded, i] : excluded.filter((v) => v !== i),
                            )
                          }
                        />{' '}
                        Escludi riga {i + 1}: {row.join(' · ')}
                      </label>
                    );
                  })}
              </div>
            </details>
            <button onClick={() => setStep(1)}>Indietro</button>
            <button
              className="sales-primary"
              disabled={!start || !end || mapping.amount < 0}
              onClick={() => {
                setStep(3);
                setConfirmed(false);
                setMessage('');
              }}
            >
              Verifica anteprima
            </button>
          </>
        )}
        {step === 3 && mapping && (
          <>
            <div className="fc-metrics">
              <article className="fc-metric">
                <span>Totale del file selezionato</span>
                <strong>{euro(totals.amount)}</strong>
                <small>
                  {mapping.basis === 'gross' ? 'IVA inclusa' : 'IVA esclusa'} · {start} – {end}
                </small>
              </article>
              <article className="fc-metric">
                <span>Ricavi netti IVA</span>
                <strong>{euro(totals.net)}</strong>
                <small>{totals.missing} righe senza IVA utilizzabile</small>
              </article>
              <article className="fc-metric">
                <span>Righe interpretabili</span>
                <strong>{preview.lines.length}</strong>
                <small>{excluded.length} escluse esplicitamente</small>
              </article>
            </div>
            {preview.errors.length > 0 && (
              <div role="alert">
                <h3>Correggi prima di importare ({preview.errors.length})</h3>
                {preview.errors.slice(0, 30).map((error, i) => (
                  <p key={i}>{error}</p>
                ))}
                <p>I totali sopra sono parziali finché esistono errori.</p>
              </div>
            )}
            {conflict && <p role="alert">{conflict}</p>}
            <details>
              <summary>Controlla le righe interpretate</summary>
              <p>
                Prime 50 righe valide. Per verificare tutte le righe usa «Modifica formato» e
                l’elenco originale paginato.
              </p>
              {preview.lines.slice(0, 50).map((line) => (
                <div className="sales-history" key={line.source_row}>
                  <strong>
                    Riga {line.source_row} · {line.item ?? 'Totale'}
                  </strong>
                  <p>
                    {line.date ?? 'Intero periodo dichiarato'} · Quantità{' '}
                    {line.quantity ?? 'non presente'} · Totale {euro(line.amount)} · IVA{' '}
                    {line.vat === null ? 'non nota' : `${line.vat}%`} · Netto {euro(line.net)}
                  </p>
                </div>
              ))}
            </details>
            {mapping.grain === 'items' && (
              <details>
                <summary>Collega gli articoli alle ricette</summary>
                <p>
                  Puoi importare ricavi anche senza collegamenti. I costi teorici richiederanno
                  ricette e quantità complete.
                </p>
                {[...new Set(preview.lines.map((line) => line.item!))].map((item) => (
                  <label key={item}>
                    {item}
                    <select
                      value={mapping.recipes[item] ?? ''}
                      onChange={(e) => {
                        const links = { ...mapping.recipes };
                        if (e.target.value) links[item] = e.target.value;
                        else delete links[item];
                        update({ recipes: links });
                      }}
                    >
                      <option value="">Non collegato</option>
                      {recipes.map((recipe) => (
                        <option key={recipe.id} value={recipe.id}>
                          {recipe.name}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </details>
            )}
            <label>
              Totale di controllo dall’estratto (stessa base IVA)
              <input
                inputMode="decimal"
                value={control}
                onChange={(e) => setControl(e.target.value)}
              />
            </label>
            <p>
              {matchingTotal
                ? 'Il totale coincide.'
                : 'Trascrivi il totale dell’estratto relativo alle righe selezionate: deve coincidere al centesimo.'}
            </p>
            <label className="sales-check">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
              />
              Confermo importi in euro, locale, periodo, IVA, totali di riga già scontati, resi con
              segno e assenza di subtotali duplicati. La fonte non include vendite già caricate da
              altre fonti.
            </label>
            <button onClick={() => setStep(2)} disabled={busy}>
              Modifica formato
            </button>
            <button
              className="sales-primary"
              onClick={() => void save()}
              disabled={
                busy ||
                !confirmed ||
                !matchingTotal ||
                !!conflict ||
                preview.errors.length > 0 ||
                batchesQuery.isPending ||
                batchesQuery.isError
              }
            >
              {busy ? 'Salvataggio…' : 'Conferma importazione'}
            </button>
          </>
        )}
      </fieldset>
      {message && (
        <p role="status" className="sales-message">
          {message}
        </p>
      )}
      <section className="fc-card">
        <h2>Storico importazioni</h2>
        {batchesQuery.isError ? (
          <p role="alert">Archivio non leggibile. Riprova prima di importare.</p>
        ) : batchesQuery.isPending ? (
          <p>Caricamento archivio…</p>
        ) : shown.length === 0 ? (
          <p>Nessun estratto caricato per questo locale.</p>
        ) : (
          shown.map((batch) => (
            <article className="sales-history" key={batch.id}>
              <h3>
                {batch.source} · {batch.period_start} – {batch.period_end}
              </h3>
              <p>
                {batch.archived_at
                  ? batch.void_confirmed_at
                    ? 'Annullata dal server'
                    : 'Annullamento in attesa di conferma'
                  : batch.confirmed_at
                    ? 'Confermata dal server'
                    : 'In attesa di conferma, esclusa dai ricavi'}{' '}
                · {batch.filename}
              </p>
              <p>
                Ricavi netti:{' '}
                {batch.confirmed_at && !batch.archived_at
                  ? euro(batch.net_total)
                  : 'Non conteggiati'}{' '}
                · {batch.lines.length} righe
              </p>
              <button
                onClick={() => {
                  setDetail(detail === batch.id ? null : batch.id);
                  setVoidReason('');
                }}
              >
                Dettagli e originale
              </button>
              {detail === batch.id && (
                <>
                  <p>
                    Formato: {batch.mapping.basis === 'net' ? 'IVA esclusa' : 'IVA inclusa'} ·{' '}
                    {batch.mapping.grain === 'items' ? 'dettaglio articoli' : 'riepilogo totali'}
                  </p>
                  <p>
                    Collegamenti ricette: {batch.lines.filter((l) => l.recipe_id).length}/
                    {batch.lines.length}. L’analisi del margine teorico non è ancora inclusa in
                    questa importazione.
                  </p>
                  <button
                    disabled={busy || !batch.confirmed_at}
                    onClick={() => void download(batch)}
                  >
                    Scarica originale
                  </button>
                  {batch.archived_at ? (
                    <p>Motivo annullamento: {batch.void_reason}</p>
                  ) : (
                    batch.confirmed_at && (
                      <>
                        <label>
                          Motivo dell’annullamento
                          <input
                            minLength={5}
                            maxLength={1000}
                            value={voidReason}
                            onChange={(e) => setVoidReason(e.target.value)}
                          />
                        </label>
                        <p>
                          Annulla solo per correggere o sostituire l’estratto. L’originale rimane
                          conservato. La richiesta viene sincronizzata quando torna la connessione.
                        </p>
                        <button
                          disabled={busy || voidReason.trim().length < 5}
                          onClick={() => void voidBatch(batch)}
                        >
                          Annulla questa importazione
                        </button>
                      </>
                    )
                  )}
                </>
              )}
            </article>
          ))
        )}
      </section>
    </section>
  );
}
